import React, { useState, useEffect, useContext } from 'react';
import { observer } from 'mobx-react';
import i18nContext from 'components/i18n-context';
import { getErrorMessageForAlert } from 'services/utils';
import Button from 'uikit/Button/Button';
import Footer from 'components/common/Footer';
import DocumentUploadStep from './DocumentUploadStep';
import Confirmation from './Confirmation';
import DocumentRequestHeader from './DocumentRequestHeader';
import { ReactComponent as CopyIcon } from 'assets/copy-icon.svg';

// Import document icons
import { ReactComponent as InvoiceIcon } from 'assets/documentRequestIcons/invoice.svg';
import { ReactComponent as AgreementIcon } from 'assets/documentRequestIcons/agreement.svg';
import { ReactComponent as BankStatementIcon } from 'assets/documentRequestIcons/bank-statement.svg';
import { ReactComponent as IdentificationIcon } from 'assets/documentRequestIcons/identification-document-of-counterparty.svg';
import { ReactComponent as ProofOfAddressIcon } from 'assets/documentRequestIcons/proof-of-aaddress-of-counterparty.svg';
import { ReactComponent as ProofOfDividendsIcon } from 'assets/documentRequestIcons/proof-of-dividends.svg';
import { ReactComponent as ProofOfWorkIcon } from 'assets/documentRequestIcons/proof-of-work.svg';
import { ReactComponent as PurposeOfPaymentIcon } from 'assets/documentRequestIcons/purpose-of-payment.svg';
import { ReactComponent as TaxDeclarationIcon } from 'assets/documentRequestIcons/tax-declaration.svg';
import { ReactComponent as TransportationIcon } from 'assets/documentRequestIcons/transportation-documents.svg';
import { ReactComponent as OtherIcon } from 'assets/documentRequestIcons/other.svg';
import { DOCUMENT_ITEM_TYPES } from 'stores/documentRequestStore';
import Alert from 'uikit/Alert/Alert';
import './styles.scss';

const StepForm = ({ documentRequestStore, userStore, isAuthorizedFlow, initialError }) => {
  const i18n = useContext(i18nContext);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [currentStep, setCurrentStep] = useState(0); // 0 = initial list, 1+ = document steps
  const [error, setError] = useState(initialError || null);
  const logoPath = process.env.REACT_APP_LOGO_PATH;

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    // TODO: Add toast notification
  };

  // Get icon for document type
  const getDocumentIcon = (type) => {
    const iconMap = {
      [DOCUMENT_ITEM_TYPES.INVOICE]: InvoiceIcon,
      [DOCUMENT_ITEM_TYPES.AGREEMENT]: AgreementIcon,
      [DOCUMENT_ITEM_TYPES.BANK_STATEMENT]: BankStatementIcon,
      [DOCUMENT_ITEM_TYPES.IDENTIFICATION_DOCUMENT]: IdentificationIcon,
      [DOCUMENT_ITEM_TYPES.PROOF_OF_ADDRESS]: ProofOfAddressIcon,
      [DOCUMENT_ITEM_TYPES.PROOF_OF_DIVIDENDS]: ProofOfDividendsIcon,
      [DOCUMENT_ITEM_TYPES.PROOF_OF_WORK]: ProofOfWorkIcon,
      [DOCUMENT_ITEM_TYPES.PURPOSE_OF_PAYMENT]: PurposeOfPaymentIcon,
      [DOCUMENT_ITEM_TYPES.TAX_DECLARATION]: TaxDeclarationIcon,
      [DOCUMENT_ITEM_TYPES.TRANSPORTATION_DOCUMENTS]: TransportationIcon,
      [DOCUMENT_ITEM_TYPES.OTHER]: OtherIcon
    };

    const Icon = iconMap[type] || OtherIcon;
    return <Icon className='document-icon' />;
  };

  // Fetch document request details on mount
  useEffect(() => {
    const fetchDetails = async () => {
      if (!documentRequestStore.requestId || !documentRequestStore.token) return;

      setIsLoadingDetails(true);
      try {
        const response = await documentRequestStore.fetchRequestByToken(
          documentRequestStore.requestId,
          documentRequestStore.token
        );

        // Transaction details are now available in the response
      } catch (error) {
        console.error('Failed to fetch document request details:', error);
      } finally {
        setIsLoadingDetails(false);
      }
    };

    fetchDetails();
  }, [documentRequestStore]);

  // Map document types to display names
  const getDocumentDisplayName = (item) => {
    if (item.type === DOCUMENT_ITEM_TYPES.OTHER && item.name) {
      return item.name;
    }

    const typeKeys = {
      [DOCUMENT_ITEM_TYPES.INVOICE]: 'documentRequest.types.invoice',
      [DOCUMENT_ITEM_TYPES.AGREEMENT]: 'documentRequest.types.agreement',
      [DOCUMENT_ITEM_TYPES.TRANSPORTATION_DOCUMENTS]: 'documentRequest.types.transportationDocuments',
      [DOCUMENT_ITEM_TYPES.PROOF_OF_WORK]: 'documentRequest.types.proofOfWork',
      [DOCUMENT_ITEM_TYPES.BANK_STATEMENT]: 'documentRequest.types.bankStatement',
      [DOCUMENT_ITEM_TYPES.TAX_DECLARATION]: 'documentRequest.types.taxDeclaration',
      [DOCUMENT_ITEM_TYPES.IDENTIFICATION_DOCUMENT]: 'documentRequest.types.identificationDocument',
      [DOCUMENT_ITEM_TYPES.PROOF_OF_ADDRESS]: 'documentRequest.types.proofOfAddress',
      [DOCUMENT_ITEM_TYPES.PROOF_OF_DIVIDENDS]: 'documentRequest.types.proofOfDividends',
      [DOCUMENT_ITEM_TYPES.PURPOSE_OF_PAYMENT]: 'documentRequest.types.purposeOfPayment',
      [DOCUMENT_ITEM_TYPES.OTHER]: 'documentRequest.types.other'
    };

    return i18n.getMessage(typeKeys[item.type]) || item.type;
  };

  const transactionId = documentRequestStore.currentRequest?.transactionId || '';

  const handleContinue = () => {
    setError(null); // Clear any existing errors
    setCurrentStep(1); // Move to first document
  };

  const handleBack = () => {
    setError(null); // Clear any existing errors
    documentRequestStore.error = null; // Clear store errors
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleNextDocument = () => {
    setError(null); // Clear any existing errors
    if (currentStep < documentRequestStore.items.length) {
      setCurrentStep(currentStep + 1);
    } else {
      // All documents uploaded, go to confirmation step
      setCurrentStep(documentRequestStore.items.length + 1);
    }
  };

  if (isLoadingDetails) {
    return (
      <div className='document-upload-container'>
        <div className='loading-state'>
          <div className='spinner' />
          <p>Loading document request...</p>
        </div>
      </div>
    );
  }

  // Show confirmation page if all documents are uploaded
  if (currentStep > documentRequestStore.items.length) {
    return (
      <Confirmation
        documentRequestStore={documentRequestStore}
        userStore={userStore}
        isAuthorizedFlow={isAuthorizedFlow}
        onBack={handleBack}
        onEditStep={(stepNumber) => setCurrentStep(stepNumber)}
      />
    );
  }

  // Show document upload step if currentStep > 0
  if (currentStep > 0 && currentStep <= documentRequestStore.items.length) {
    const currentItem = documentRequestStore.items[currentStep - 1];
    return (
      <DocumentUploadStep
        key={`step-${currentStep}`} // This will force React to remount the component
        documentRequestStore={documentRequestStore}
        userStore={userStore}
        isAuthorizedFlow={isAuthorizedFlow}
        item={currentItem}
        currentStep={currentStep}
        totalSteps={documentRequestStore.items.length}
        onBack={handleBack}
        onContinue={handleNextDocument}
      />
    );
  }

  // Show initial list page
  return (
    <div className='document-request-container'>
      <div className='document-request-header'>
        <a href={process.env.REACT_APP_LINK_HOME_PAGE} className='logo'>
          <img src={logoPath} alt='Logo' className='icon-logo' />
        </a>
      </div>

      <div className='document-request-content'>
        <div className='content-header'>
          <h1>{i18n.getMessage('documentRequest.title')}</h1>
          {transactionId && (
            <div className='transaction-badge'>
              {i18n.getMessage('documentRequest.transaction')} #{transactionId}
            </div>
          )}

          <DocumentRequestHeader
            expiresAt={documentRequestStore.expiresAt}
            transactionId={documentRequestStore.currentRequest?.transactionNumber || null}
            showDeadline={false}
            showTransaction={true}
          />
        </div>

        <div className='transaction-details-section'>
          <div className='transaction-details-content'>
            {/* Counterparty */}
            {documentRequestStore.currentRequest?.partyName && (
              <div className='detail-row'>
                <span className='detail-label'>{i18n.getMessage('documentRequest.counterparty')}</span>
                <span className='detail-value'>{documentRequestStore.currentRequest.partyName}</span>
              </div>
            )}
            {/* Amount */}
            {documentRequestStore.currentRequest?.amountDetails?.displayAmount && (
              <div className='detail-row'>
                <span className='detail-label'>{i18n.getMessage('documentRequest.amount')}</span>
                <span className='detail-value'>{documentRequestStore.currentRequest.amountDetails.displayAmount}</span>
              </div>
            )}
            {/* Reference */}
            {documentRequestStore.currentRequest?.comment && (
              <div className='detail-row'>
                <span className='detail-label'>{i18n.getMessage('documentRequest.reference')}</span>
                <div className='detail-value-with-action'>
                  <span className='detail-value'>{documentRequestStore.currentRequest.comment}</span>
                  <button
                    className='copy-button'
                    onClick={() => copyToClipboard(documentRequestStore.currentRequest.comment)}
                  >
                    <CopyIcon />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Error Alert */}
        {(error || documentRequestStore.error) && (
          <Alert
            type='error'
            message={
              error ||
              getErrorMessageForAlert(i18n, documentRequestStore.error, true, null, null) ||
              'An error occurred'
            }
          />
        )}

        <div className='documents-list'>
          {documentRequestStore.items && documentRequestStore.items.length > 0 ? (
            documentRequestStore.items.map((item, index) => (
              <div key={`${item.type}-${index}`} className='document-item'>
                <div className='document-icon-container'>{getDocumentIcon(item.type)}</div>
                <div className='document-info'>
                  <div className='document-name'>
                    <span className='document-index'>{index + 1}. </span>
                    {getDocumentDisplayName(item)}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className='no-documents'>{i18n.getMessage('documentRequest.noDocuments')}</div>
          )}
        </div>

        <Button type='primary' size='large' fullWidth onClick={handleContinue} className='continue-button'>
          {i18n.getMessage('documentRequest.continueToUpload')}
        </Button>
      </div>

      <Footer />
    </div>
  );
};

export default observer(StepForm);
